import type { MachineJourneyId } from '@wapharma/domain';
import type { CapabilityPack } from '@wapharma/domain';
import { CAPABILITY_PACK } from '@wapharma/domain';
import { ADMIN_LOTS, ADMIN_LOCATIONS, ADMIN_PRODUCTS, ADMIN_ROLES, ADMIN_USERS } from '../constants/adminNavLabels';

export type JourneyStep = {
  id: string;
  title: string;
  href: string;
  hint?: string;
};

export type JourneyPack = {
  id: MachineJourneyId;
  order: number;
  title: string;
  shortTitle: string;
  requiredPacks: readonly CapabilityPack[];
  steps: JourneyStep[];
  stub?: boolean;
};

export const JOURNEY_PACKS: JourneyPack[] = [
  {
    id: 'people',
    order: 0,
    title: 'Personnes et autorité',
    shortTitle: 'Personnes',
    requiredPacks: [CAPABILITY_PACK.PARTY_ADMIN],
    steps: [
      {
        id: 'people-users',
        title: 'Comptes utilisateurs',
        href: ADMIN_USERS.path,
        hint: 'Un compte par personne. Pas de compte partagé.',
      },
      {
        id: 'people-roles',
        title: 'Rôles et accès',
        href: ADMIN_ROLES.path,
        hint: 'Chaque rôle voit et fait seulement ce qui lui correspond.',
      },
    ],
  },
  {
    id: 'catalog',
    order: 1,
    title: 'Identité catalogue',
    shortTitle: 'Catalogue',
    requiredPacks: [CAPABILITY_PACK.CATALOG_MAINTAIN],
    steps: [
      {
        id: 'catalog-product',
        title: 'Produit et variante',
        href: ADMIN_PRODUCTS.path,
        hint: 'Créer le produit ici avant de recevoir du stock.',
      },
      {
        id: 'catalog-ready',
        title: 'Marquer prêt',
        href: ADMIN_PRODUCTS.path,
        hint: 'Prêt à vendre en ligne ne veut pas dire stock vendable.',
      },
    ],
  },
  {
    id: 'locations',
    order: 2,
    title: 'Emplacements et classes',
    shortTitle: 'Emplacements',
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    steps: [
      {
        id: 'locations-list',
        title: 'Emplacements entrepôt',
        href: ADMIN_LOCATIONS.path,
        hint: 'Où le stock est rangé.',
      },
      {
        id: 'locations-class',
        title: 'Classes de stock',
        href: ADMIN_LOTS.path,
        hint: 'Seul le vendable entre dans la quantité commandable.',
      },
    ],
  },
  {
    id: 'buy',
    order: 3,
    title: 'Achat',
    shortTitle: 'Achat',
    requiredPacks: [CAPABILITY_PACK.BUY_PROCURE],
    steps: [
      {
        id: 'buy-qualify',
        title: 'Qualifier un fournisseur',
        href: '/procurement/suppliers',
      },
      {
        id: 'buy-po',
        title: 'Bon de commande',
        href: '/procurement/purchase-orders',
        hint: 'Nécessaire pour réceptionner.',
      },
    ],
  },
  {
    id: 'receive',
    order: 4,
    title: 'Réception quarantaine',
    shortTitle: 'Réception',
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    steps: [
      {
        id: 'receive-po',
        title: 'Réceptionner',
        href: '/procurement/purchase-orders',
        hint: 'Entrée en quarantaine par défaut.',
      },
      {
        id: 'receive-lot',
        title: 'Contrôler le lot',
        href: ADMIN_LOTS.path,
        hint: 'Vendable seulement après libération.',
      },
    ],
  },
  {
    id: 'release',
    order: 5,
    title: 'Libération vendable',
    shortTitle: 'Libération',
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    steps: [
      {
        id: 'release-lot',
        title: 'Libérer le lot',
        href: ADMIN_LOTS.path,
        hint: 'Après contrôle qualité.',
      },
      {
        id: 'release-promise',
        title: 'Quantité commandable',
        href: ADMIN_PRODUCTS.path,
        hint: 'Vérifiez le chiffre affiché sur la fiche produit.',
      },
    ],
  },
  {
    id: 'sell',
    order: 6,
    title: 'Vente',
    shortTitle: 'Vente',
    requiredPacks: [CAPABILITY_PACK.SELL_COMMIT],
    steps: [
      {
        id: 'sell-order',
        title: 'Commande client',
        href: '/orders',
        hint: 'Stock vendable libéré requis.',
      },
      {
        id: 'sell-confirm',
        title: 'Confirmer et expédier',
        href: '/orders',
        hint: 'Réservation à la confirmation, sortie stock à l expédition.',
      },
      {
        id: 'sell-deliver',
        title: 'Livrer',
        href: '/orders',
        hint: 'Si votre canal le demande.',
      },
    ],
  },
  {
    id: 'return',
    order: 7,
    title: 'Retour et rappel',
    shortTitle: 'Retour',
    requiredPacks: [CAPABILITY_PACK.STOCK_MOVE],
    steps: [
      {
        id: 'return-order',
        title: 'Retour commande',
        href: '/orders',
        hint: 'Après une expédition enregistrée.',
      },
      {
        id: 'return-assess',
        title: 'Évaluer le lot',
        href: ADMIN_LOTS.path,
      },
      {
        id: 'return-stop',
        title: 'Arrêt lot (rappel)',
        href: ADMIN_LOTS.path,
        hint: 'Lot rappelé : ne pas remettre en vendable.',
      },
    ],
  },
  {
    id: 'money',
    order: 8,
    title: 'Clôture période',
    shortTitle: 'Clôture',
    requiredPacks: [CAPABILITY_PACK.MONEY_READ, CAPABILITY_PACK.MONEY_POST],
    stub: true,
    steps: [
      {
        id: 'money-coa',
        title: 'Plan comptable',
        href: '/accounting',
        hint: 'Fonction encore limitée.',
      },
      {
        id: 'money-honesty',
        title: 'Exports et rapports',
        href: '/',
        hint: 'Clôture réelle : validation finance.',
      },
    ],
  },
];

export function journeyPackById(id: MachineJourneyId): JourneyPack | undefined {
  return JOURNEY_PACKS.find((p) => p.id === id);
}

export function journeyPackTitle(id: MachineJourneyId): string {
  return journeyPackById(id)?.title ?? id;
}

/** One navigable step per pack — first defined step. */
export function primaryStepForPack(pack: JourneyPack) {
  return pack.steps[0] ?? null;
}

export function filterJourneyPacksForUser(
  packs: readonly CapabilityPack[],
  opts?: { isSuperAdmin?: boolean },
): JourneyPack[] {
  const set = new Set(packs);
  const all = [...JOURNEY_PACKS].sort((a, b) => a.order - b.order);
  if (opts?.isSuperAdmin) {
    // Money stub stays visible for preview; unavailable flows filtered in packFlows.
    return all;
  }
  return all.filter((p) => {
    if (p.stub) return false;
    return p.requiredPacks.some((req) => set.has(req));
  });
}
